import { useEffect, useMemo, useState } from "react";
import "./prism-correlation-map.css";

type NodeRecord = {
  problem_instance_id: string;
  normalized_hash: string;
  origin_object_id: string;
  problem_type: string;
  jurisdiction: string;
  slice_id: string;
  risk_level: string | null;
  provenance_integrity: string;
  source_validation_state: string;
};

type EdgeRecord = {
  pair_candidate_id: string;
  candidate_hash: string;
  problem_a_id: string;
  problem_b_id: string;
  pairing_score: number;
  threshold: number;
  cross_slice: boolean;
  pairing_dimensions: string[];
  pairing_engine_version: string;
  state: string;
  correlation_id: string | null;
  correlation_state: string | null;
  verification_mode: string | null;
};

type Snapshot = {
  schema_version: "prism-correlation-map-1.0.0";
  pairing_engine_version: string;
  candidate_is_verified_correlation: false;
  nodes: NodeRecord[];
  edges: EdgeRecord[];
  totals: {
    active_problem_instances: number;
    provenance_verified_problem_instances: number;
    eligible_pair_candidates: number;
    cross_slice_pair_candidates: number;
    verified_correlations: number;
  };
  exported_at: string;
};

type Overview = {
  schema_version: string;
  candidate_is_verified_correlation: false;
  correlation_map: Snapshot;
};

type Point = { x: number; y: number };
type EdgeMode = "all" | "cross" | "verified";

function short(value: string, n = 11) {
  return value.length > n ? `${value.slice(0, n)}…` : value;
}

function deterministicPositions(nodes: NodeRecord[]): Map<string, Point> {
  const result = new Map<string, Point>();
  const grouped = new Map<string, NodeRecord[]>();
  for (const node of nodes) {
    const key = node.slice_id || "Unknown";
    const group = grouped.get(key) || [];
    group.push(node);
    grouped.set(key, group);
  }
  const slices = [...grouped.keys()].sort((a, b) => a.localeCompare(b));
  const cx = 600;
  const cy = 365;
  const ringX = 400;
  const ringY = 255;
  slices.forEach((slice, sliceIndex) => {
    const theta = (sliceIndex / Math.max(1, slices.length)) * Math.PI * 2 - Math.PI / 2;
    const sx = cx + Math.cos(theta) * ringX;
    const sy = cy + Math.sin(theta) * ringY;
    const group = (grouped.get(slice) || []).sort((a, b) => a.normalized_hash.localeCompare(b.normalized_hash));
    group.forEach((node, nodeIndex) => {
      const localTheta = (nodeIndex / Math.max(1, group.length)) * Math.PI * 2;
      const radius = group.length === 1 ? 0 : 24 + Math.min(26, group.length * 3);
      result.set(node.problem_instance_id, {
        x: sx + Math.cos(localTheta) * radius,
        y: sy + Math.sin(localTheta) * radius,
      });
    });
  });
  return result;
}

function Metric({ label, value, note }: { label: string; value: number | string; note: string }) {
  return <article className="pcm-card pcm-metric"><span>{label}</span><b>{value}</b><small>{note}</small></article>;
}

export default function PrismCorrelationMap() {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [mode, setMode] = useState<EdgeMode>("cross");
  const [minimumScore, setMinimumScore] = useState(30);

  useEffect(() => {
    let active = true;
    fetch("/api/v1/problems/overview", { headers: { accept: "application/json" } })
      .then(async (response) => {
        if (!response.ok) throw new Error(`problem_overview_http_${response.status}`);
        return response.json() as Promise<Overview>;
      })
      .then((overview) => {
        if (!overview.correlation_map || overview.candidate_is_verified_correlation !== false) {
          throw new Error("correlation_map_contract_invalid");
        }
        if (active) {
          setSnapshot(overview.correlation_map);
          const scores = overview.correlation_map.edges.map((edge) => edge.pairing_score);
          if (scores.length) setMinimumScore(Math.min(...scores));
        }
      })
      .catch((reason) => { if (active) setError(reason instanceof Error ? reason.message : String(reason)); });
    return () => { active = false; };
  }, []);

  const positions = useMemo(() => deterministicPositions(snapshot?.nodes || []), [snapshot?.nodes]);
  const nodeById = useMemo(() => new Map((snapshot?.nodes || []).map((node) => [node.problem_instance_id, node])), [snapshot?.nodes]);
  const scores = useMemo(() => (snapshot?.edges || []).map((edge) => edge.pairing_score), [snapshot?.edges]);
  const scoreFloor = scores.length ? Math.min(...scores) : 0;
  const scoreCeiling = scores.length ? Math.max(...scores) : 100;
  const visibleEdges = useMemo(() => (snapshot?.edges || []).filter((edge) => {
    const verified = edge.correlation_state === "VERIFIED" && edge.verification_mode === "BIDIRECTIONALLY_VERIFIED";
    if (edge.pairing_score < minimumScore) return false;
    if (mode === "cross" && !edge.cross_slice) return false;
    if (mode === "verified" && !verified) return false;
    return true;
  }), [snapshot?.edges, minimumScore, mode]);
  const selectedNode = selected ? nodeById.get(selected) || null : null;
  const selectedEdges = selected ? visibleEdges.filter((edge) => edge.problem_a_id === selected || edge.problem_b_id === selected) : [];

  if (error) return <main className="pcm-main"><section className="pcm-card pcm-error"><h1>Correlation Map unavailable</h1><p>Prism failed closed rather than falling back to the obsolete case-correlation heuristic.</p><code>{error}</code></section></main>;
  if (!snapshot) return <main className="pcm-main"><section className="pcm-card pcm-loading"><div className="pcm-spinner" /><h1>Loading current Prism correlation map</h1><p>Reading normalized problem instances and current pairing candidates through Prism&apos;s server boundary.</p></section></main>;

  return <main className="pcm-main">
    <section className="pcm-hero">
      <div><span className="pcm-kicker">CURRENT PROBLEM RELATIONSHIP GRAPH</span><h1>Correlation Map</h1><p>Explore the current deterministic pairing network across normalized Prism problem instances. Candidate links identify relationships worth independent traversal; they are not causal findings.</p></div>
      <div className="pcm-engine"><span>Pairing engine</span><b>{snapshot.pairing_engine_version}</b><small>Schema {snapshot.schema_version}</small></div>
    </section>

    <section className="pcm-warning"><b>Candidate ≠ verified correlation.</b><span>{snapshot.totals.verified_correlations} bidirectionally verified causal correlations currently exist. Candidate edges remain pre-verification work.</span></section>

    <section className="pcm-metrics">
      <Metric label="Problem instances" value={snapshot.totals.active_problem_instances} note={`${snapshot.totals.provenance_verified_problem_instances} provenance verified`} />
      <Metric label="Current candidates" value={snapshot.totals.eligible_pair_candidates} note="current pairing-engine queue" />
      <Metric label="Cross-slice" value={snapshot.totals.cross_slice_pair_candidates} note="bounded structural candidates" />
      <Metric label="Verified correlations" value={snapshot.totals.verified_correlations} note="bidirectional convergence only" />
    </section>

    <section className="pcm-controls pcm-card">
      <div className="pcm-segment" role="group" aria-label="Edge mode">
        <button className={mode === "cross" ? "active" : ""} onClick={() => setMode("cross")}>Cross-slice</button>
        <button className={mode === "all" ? "active" : ""} onClick={() => setMode("all")}>All candidates</button>
        <button className={mode === "verified" ? "active" : ""} onClick={() => setMode("verified")}>Verified only</button>
      </div>
      <label><span>Minimum pairing score <b>{minimumScore}</b></span><input type="range" min={scoreFloor} max={scoreCeiling} step="1" value={minimumScore} onChange={(event) => setMinimumScore(Number(event.target.value))} /></label>
      <strong>{visibleEdges.length} displayed edges</strong>
    </section>

    <section className="pcm-layout">
      <article className="pcm-card pcm-map">
        <svg viewBox="0 0 1200 730" role="img" aria-label="Prism candidate correlation network">
          {visibleEdges.map((edge) => {
            const a = positions.get(edge.problem_a_id);
            const b = positions.get(edge.problem_b_id);
            if (!a || !b) return null;
            const verified = edge.correlation_state === "VERIFIED" && edge.verification_mode === "BIDIRECTIONALLY_VERIFIED";
            const className = verified ? "verified" : edge.cross_slice ? "cross" : "same";
            return <line key={edge.candidate_hash} className={className} x1={a.x} y1={a.y} x2={b.x} y2={b.y} strokeWidth={verified ? 3 : Math.max(0.7, edge.pairing_score / 25)} />;
          })}
          {snapshot.nodes.map((node) => {
            const point = positions.get(node.problem_instance_id);
            if (!point) return null;
            const verified = node.provenance_integrity === "VERIFIED";
            return <g key={node.problem_instance_id} className={`${selected === node.problem_instance_id ? "selected" : ""} ${verified ? "provenance-verified" : "provenance-open"}`} transform={`translate(${point.x} ${point.y})`} onClick={() => setSelected(node.problem_instance_id)}>
              <circle r={selected === node.problem_instance_id ? 10 : 7} />
              <title>{`${node.slice_id} · ${node.jurisdiction} · ${node.problem_type}`}</title>
            </g>;
          })}
        </svg>
        <div className="pcm-legend"><span><i className="cross" />Cross-slice candidate</span><span><i className="same" />Same-slice candidate</span><span><i className="verified" />Bidirectionally verified</span><span><i className="node-open" />Incomplete provenance</span></div>
      </article>

      <aside className="pcm-card pcm-detail">
        {selectedNode ? <>
          <span className="pcm-kicker">SELECTED PROBLEM INSTANCE</span><h2>{selectedNode.slice_id}</h2><p className="pcm-subtitle">{selectedNode.jurisdiction} · {selectedNode.problem_type}</p>
          <dl><div><dt>Origin</dt><dd>{selectedNode.origin_object_id}</dd></div><div><dt>Provenance</dt><dd>{selectedNode.provenance_integrity}</dd></div><div><dt>Source validation</dt><dd>{selectedNode.source_validation_state}</dd></div><div><dt>Current visible links</dt><dd>{selectedEdges.length}</dd></div></dl>
          <h3>Strongest displayed links</h3>
          <div className="pcm-edge-list">{selectedEdges.sort((a, b) => b.pairing_score - a.pairing_score).slice(0, 8).map((edge) => {
            const peerId = edge.problem_a_id === selectedNode.problem_instance_id ? edge.problem_b_id : edge.problem_a_id;
            const peer = nodeById.get(peerId);
            return <button key={edge.candidate_hash} onClick={() => setSelected(peerId)}><span><b>{peer?.slice_id || short(peerId)}</b><small>{peer?.jurisdiction || "Unknown jurisdiction"}</small></span><strong>{edge.pairing_score}</strong></button>;
          })}</div>
          {!selectedEdges.length ? <p className="pcm-empty">No links match the current map filter.</p> : null}
        </> : <><span className="pcm-kicker">INSPECT THE NETWORK</span><h2>Select a node</h2><p>Nodes are deterministic normalized problem instances. Edge selection is bounded and versioned; correlation requires separate bidirectional verification.</p><dl><div><dt>Nodes</dt><dd>{snapshot.nodes.length}</dd></div><div><dt>Displayed edges</dt><dd>{visibleEdges.length}</dd></div><div><dt>Snapshot</dt><dd>{new Date(snapshot.exported_at).toLocaleString()}</dd></div></dl></>}
      </aside>
    </section>
  </main>;
}
